var _a, _b;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { addMonths, compareISO, dayBadge, formatMonthLabel, formatMonthRange, monthOf, } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { Box, IconButton, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { deriveStatus, getMonthGrid } from './monthGrid.util';
var STRINGS = appStrings.modules.monthCalendar;
var STATUS_VISUALS = {
    upcoming: {},
    due: {},
    overdue: { icon: 'AlertCircle', iconColor: 'Colors/Foreground/fg-error-primary' },
    completed: { icon: 'CheckCircle', iconColor: 'Colors/Foreground/fg-success-primary' },
};
var TONE_VISUALS = {
    brand: { bg: 'Brand/50', fg: 'Brand/700', border: 'Brand/200', dot: 'Brand/500' },
    indigo: { bg: 'Indigo/50', fg: 'Indigo/700', border: 'Indigo/200', dot: 'Indigo/500' },
    blue: { bg: 'Blue/50', fg: 'Blue/700', border: 'Blue/200', dot: 'Blue/500' },
    orange: { bg: 'Orange/50', fg: 'Orange/700', border: 'Orange/200', dot: 'Orange/500' },
    teal: { bg: 'Teal/50', fg: 'Teal/700', border: 'Teal/200', dot: 'Teal/500' },
    pink: { bg: 'Pink/50', fg: 'Pink/700', border: 'Pink/200', dot: 'Pink/500' },
    green: { bg: 'Green/50', fg: 'Green/700', border: 'Green/200', dot: 'Green/500' },
    yellow: { bg: 'Yellow/50', fg: 'Yellow/700', border: 'Yellow/200', dot: 'Yellow/500' },
    neutral: {
        bg: 'Colors/Background/bg-secondary',
        fg: 'Colors/Text/text-secondary',
        border: 'Colors/Border/border-secondary',
        dot: 'Colors/Foreground/fg-secondary',
    },
};
var MAX_VISIBLE_ENTRIES = 2;
var WEEKDAYS = [
    { full: 'Mon', short: 'Mon' },
    { full: 'Tues', short: 'T' },
    { full: 'Wed', short: 'W' },
    { full: 'Thu', short: 'T' },
    { full: 'Fri', short: 'F' },
    { full: 'Sat', short: 'S' },
    { full: 'Sun', short: 'S' },
];
function matchesDatePattern(value, pattern) {
    var parsed = parseISO(value);
    return isValid(parsed) && format(parsed, pattern) === value;
}
function findInvalidDateProp(_a) {
    var visibleMonth = _a.visibleMonth, today = _a.today, startDate = _a.startDate, days = _a.days;
    if (!matchesDatePattern(visibleMonth, 'yyyy-MM')) {
        return "visibleMonth \"".concat(visibleMonth, "\" is not a valid YYYY-MM month");
    }
    if (!matchesDatePattern(today, 'yyyy-MM-dd')) {
        return "today \"".concat(today, "\" is not a valid YYYY-MM-DD date");
    }
    if (!matchesDatePattern(startDate, 'yyyy-MM-dd')) {
        return "startDate \"".concat(startDate, "\" is not a valid YYYY-MM-DD date");
    }
    var badDay = days.find(function (day) { return !matchesDatePattern(day.date, 'yyyy-MM-dd'); });
    return badDay ? "days[].date \"".concat(badDay.date, "\" is not a valid YYYY-MM-DD date") : null;
}
export function MonthCalendar(_a) {
    var _b = _a.id, id = _b === void 0 ? 'month-calendar' : _b, startDate = _a.startDate, today = _a.today, days = _a.days, visibleMonth = _a.visibleMonth, onMonthChange = _a.onMonthChange, onDayClick = _a.onDayClick, headerAction = _a.headerAction, emptyState = _a.emptyState;
    var invalidDateProp = useMemo(function () {
        var message = findInvalidDateProp({ visibleMonth: visibleMonth, today: today, startDate: startDate, days: days });
        if (message) {
            console.warn("[MonthCalendar] ".concat(message, " \u2014 rendering the empty state instead."));
        }
        return message;
    }, [visibleMonth, today, startDate, days]);
    var grid = useMemo(function () { return (invalidDateProp ? [] : getMonthGrid(visibleMonth)); }, [invalidDateProp, visibleMonth]);
    var dayByISO = useMemo(function () {
        var map = new Map();
        days.forEach(function (day) { return map.set(day.date, day); });
        return map;
    }, [days]);
    if (invalidDateProp) {
        return (_jsx(Box, { id: id, sx: rootSx, children: _jsx(Typography, { variant: "text-sm-regular", sx: emptyStateSx, children: emptyState }) }));
    }
    var isEmpty = days.length === 0;
    return (_jsxs(Box, { id: id, sx: rootSx, children: [_jsx(CalendarHeader, { id: id, title: formatMonthLabel(visibleMonth), range: formatMonthRange(visibleMonth), badge: dayBadge(today), onPrev: function () { return onMonthChange(addMonths(visibleMonth, -1)); }, onNext: function () { return onMonthChange(addMonths(visibleMonth, 1)); }, onToday: function () { return onMonthChange(monthOf(today)); }, headerAction: headerAction }), _jsxs(Box, { sx: gridShellSx, role: "grid", children: [WEEKDAYS.map(function (weekday, index) { return (_jsxs(Box, { sx: weekdayHeadSx, role: "columnheader", children: [_jsx(Typography, { variant: "text-xs-medium", sx: weekdayFullSx, children: weekday.full }), _jsx(Typography, { variant: "text-xs-medium", sx: weekdayShortSx, children: weekday.short })] }, index)); }), grid.map(function (cell) {
                        var day = dayByISO.get(cell.iso);
                        var isToday = cell.iso === today;
                        if (!day) {
                            return (_jsx(Box, { sx: inertCellSx, role: "gridcell", children: _jsx(DayNumber, { value: cell.dayOfMonth, isToday: isToday, muted: true }) }, cell.iso));
                        }
                        var status = deriveStatus(day, today);
                        var isPast = compareISO(cell.iso, today) < 0;
                        return (_jsx(DayCell, { id: "".concat(id, "-day-").concat(cell.iso), dayOfMonth: cell.dayOfMonth, day: day, status: status, isToday: isToday, dimmed: isPast && status !== 'overdue', outOfMonth: !cell.inCurrentMonth, onClick: function () {
                                if (!cell.inCurrentMonth)
                                    onMonthChange(monthOf(cell.iso));
                                onDayClick(cell.iso);
                            } }, cell.iso));
                    })] }), isEmpty ? (_jsx(Typography, { variant: "text-sm-regular", sx: emptyStateSx, children: emptyState })) : null] }));
}
function CalendarHeader(_a) {
    var id = _a.id, title = _a.title, range = _a.range, badge = _a.badge, onPrev = _a.onPrev, onNext = _a.onNext, onToday = _a.onToday, headerAction = _a.headerAction;
    return (_jsxs(Box, { sx: headerSx, children: [_jsxs(Box, { sx: headerLeftSx, children: [_jsxs(Box, { sx: badgeBoxSx, "aria-hidden": true, children: [_jsx(Typography, { variant: "text-xs-semibold", sx: badgeMonthSx, children: badge.month }), _jsx(Typography, { variant: "text-lg-bold", sx: badgeDaySx, children: badge.day })] }), _jsxs(Box, { sx: titleBlockSx, children: [_jsx(Typography, { variant: "text-lg-bold", sx: titleSx, children: title }), _jsx(Typography, { variant: "text-sm-regular", sx: rangeSx, children: range })] })] }), _jsxs(Box, { sx: headerNavSx, children: [_jsxs(Box, { sx: navGroupSx, children: [_jsx(IconButton, { id: "".concat(id, "-prev-month"), "aria-label": STRINGS.previousMonth, onClick: onPrev, sx: navButtonSx, children: _jsx(AppIcon, { iconName: "ChevronLeft", diameter: "20px", color: "Colors/Foreground/fg-secondary" }) }), _jsx(Box, { component: "button", id: "".concat(id, "-today"), type: "button", onClick: onToday, sx: todayButtonSx, children: _jsx(Typography, { variant: "text-sm-semibold", sx: todayButtonTextSx, children: STRINGS.today }) }), _jsx(IconButton, { id: "".concat(id, "-next-month"), "aria-label": STRINGS.nextMonth, onClick: onNext, sx: navButtonSx, children: _jsx(AppIcon, { iconName: "ChevronRight", diameter: "20px", color: "Colors/Foreground/fg-secondary" }) })] }), headerAction] })] }));
}
function DayNumber(_a) {
    var value = _a.value, isToday = _a.isToday, muted = _a.muted;
    if (isToday) {
        return (_jsx(Box, { sx: todayBadgeSx, children: _jsx(Typography, { variant: "text-xs-semibold", sx: todayBadgeTextSx, children: value }) }));
    }
    return (_jsx(Typography, { variant: "text-sm-medium", sx: muted ? inertNumberSx : dayNumberSx, children: value }));
}
function DayCell(_a) {
    var _b, _c, _d, _e;
    var id = _a.id, dayOfMonth = _a.dayOfMonth, day = _a.day, status = _a.status, isToday = _a.isToday, dimmed = _a.dimmed, outOfMonth = _a.outOfMonth, onClick = _a.onClick;
    var kind = day.kindVisual;
    var statusVisual = (_b = STATUS_VISUALS[status]) !== null && _b !== void 0 ? _b : STATUS_VISUALS.upcoming;
    var kindLabel = (_e = (_c = kind === null || kind === void 0 ? void 0 : kind.label) !== null && _c !== void 0 ? _c : (_d = day.entries) === null || _d === void 0 ? void 0 : _d.map(function (entry) { return entry.label; }).join(', ')) !== null && _e !== void 0 ? _e : '';
    return (_jsxs(Box, { component: "button", id: id, type: "button", onClick: onClick, "aria-label": STRINGS.dayAriaLabel(kindLabel, dayOfMonth, status, outOfMonth ? dayBadge(day.date).month : undefined), sx: dayCellSx(dimmed, outOfMonth), children: [_jsxs(Box, { sx: cellTopRowSx, children: [_jsx(DayNumber, { value: dayOfMonth, isToday: isToday }), statusVisual.icon && statusVisual.iconColor ? (_jsx(AppIcon, { iconName: statusVisual.icon, diameter: "16px", color: statusVisual.iconColor })) : null] }), kind ? (_jsxs(Box, { sx: kindPillSx(kind), children: [kind.icon ? _jsx(AppIcon, { iconName: kind.icon, diameter: "12px", color: "currentColor" }) : null, _jsx(Typography, { variant: "text-xs-semibold", sx: kindPillTextSx, noWrap: true, children: kind.label })] })) : null, day.entries && day.entries.length > 0 ? (_jsx(EntryPills, { entries: day.entries, completed: day.completed })) : null, day.focus ? (_jsx(Typography, { variant: "text-xs-medium", sx: focusChipSx, noWrap: true, children: day.focus })) : null, day.note ? (_jsx(Typography, { variant: "text-xs-medium", sx: noteChipSx, noWrap: true, title: day.note, children: day.note })) : null] }));
}
function EntryPills(_a) {
    var entries = _a.entries, completed = _a.completed;
    var visible = entries.slice(0, MAX_VISIBLE_ENTRIES);
    var overflow = entries.length - visible.length;
    return (_jsxs(Box, { sx: entryListSx, children: [visible.map(function (entry, index) {
                var _a;
                var tone = (_a = TONE_VISUALS[entry.tone]) !== null && _a !== void 0 ? _a : TONE_VISUALS.neutral;
                return (_jsxs(Box, { sx: entryPillSx(tone), children: [!completed && _jsx(Box, { sx: entryDotSx(tone), "aria-hidden": true }), _jsx(Typography, { variant: "text-xs-semibold", sx: entryPillTextSx, children: entry.label })] }, "".concat(entry.label, "-").concat(index)));
            }), overflow > 0 ? (_jsx(Typography, { variant: "text-xs-medium", sx: moreEntriesSx, noWrap: true, children: STRINGS.moreEntries(overflow) })) : null] }));
}
var MOBILE = '@media (max-width: 600px)';
var rootSx = function () { return ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: 1,
    minHeight: 0,
    boxSizing: 'border-box',
}); };
var headerSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: backrs.spacing['spacing-lg'],
            flexWrap: 'wrap',
            padding: backrs.spacing['spacing-2xl'],
            borderBottom: "1px solid ".concat(backrs.colors['Colors/Border/border-secondary'])
        },
        _b[MOBILE] = { padding: backrs.spacing['spacing-lg'] },
        _b);
};
var headerLeftSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-lg'],
    });
};
var badgeBoxSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '48px',
        padding: backrs.spacing['spacing-xs'],
        border: "1px solid ".concat(backrs.colors['Colors/Border/border-secondary']),
        borderRadius: backrs.radius['radius-md'],
    });
};
var badgeMonthSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-quaternary'],
        textTransform: 'uppercase',
        lineHeight: 1.2,
    });
};
var badgeDaySx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-brand-secondary'],
        lineHeight: 1.1,
    });
};
var titleBlockSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-xxs'],
    });
};
var titleSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-primary'],
    });
};
var rangeSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-tertiary'],
    });
};
var headerNavSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-lg'],
    });
};
var navGroupSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        border: "1px solid ".concat(backrs.colors['Colors/Border/border-primary']),
        borderRadius: backrs.radius['radius-md'],
        boxShadow: backrs.shadows['shadow-xs'],
    });
};
var navButtonSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        padding: backrs.spacing['spacing-xs'],
        borderRadius: 0,
        borderRight: "1px solid ".concat(backrs.colors['Colors/Border/border-primary']),
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
        '&:last-of-type': { borderRight: 'none' },
    });
};
var todayButtonSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        padding: "".concat(backrs.spacing['spacing-xs'], " ").concat(backrs.spacing['spacing-md']),
        borderRadius: 0,
        border: 'none',
        borderRight: "1px solid ".concat(backrs.colors['Colors/Border/border-primary']),
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
        cursor: 'pointer',
        font: 'inherit',
        '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-secondary'] },
        '&:focus-visible': {
            outline: "2px solid ".concat(backrs.colors['Colors/Foreground/fg-brand-primary']),
            outlineOffset: '-2px',
        },
    });
};
var todayButtonTextSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-secondary'],
    });
};
var gridShellSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gridTemplateRows: 'auto',
            gridAutoRows: 'minmax(112px, 1fr)',
            gap: '1px',
            flex: 1,
            minHeight: 0,
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: backrs.colors['Colors/Border/border-secondary']
        },
        _b[MOBILE] = {
            gridAutoRows: 'minmax(60px, 1fr)',
        },
        _b);
};
var weekdayHeadSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        minWidth: 0,
        padding: "".concat(backrs.spacing['spacing-xs'], " ").concat(backrs.spacing['spacing-sm']),
        textAlign: 'center',
        color: backrs.colors['Colors/Text/text-tertiary'],
        backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    });
};
var weekdayFullSx = (_a = { color: 'inherit' }, _a[MOBILE] = { display: 'none' }, _a);
var weekdayShortSx = (_b = {
        color: 'inherit',
        display: 'none'
    },
    _b[MOBILE] = { display: 'block' },
    _b);
var inertCellSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            minWidth: 0,
            minHeight: '112px',
            padding: backrs.spacing['spacing-sm'],
            backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
            boxSizing: 'border-box'
        },
        _b[MOBILE] = { minHeight: '60px', padding: backrs.spacing['spacing-xs'] },
        _b);
};
var dayCellSx = function (dimmed, outOfMonth) {
    return function (_a) {
        var _b;
        var backrs = _a.backrs;
        return (_b = {
                display: 'flex',
                flexDirection: 'column',
                gap: backrs.spacing['spacing-xs'],
                minWidth: 0,
                minHeight: '112px',
                padding: backrs.spacing['spacing-sm'],
                textAlign: 'left',
                boxSizing: 'border-box',
                overflow: 'hidden',
                backgroundColor: outOfMonth
                    ? backrs.colors['Colors/Background/bg-secondary-subtle']
                    : backrs.colors['Colors/Background/bg-primary'],
                color: backrs.colors['Colors/Text/text-primary'],
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                opacity: dimmed ? 0.55 : 1,
                transition: 'background-color 0.1s ease',
                '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-active'] },
                '&:focus-visible': {
                    outline: "2px solid ".concat(backrs.colors['Colors/Foreground/fg-brand-primary']),
                    outlineOffset: '-2px',
                }
            },
            _b[MOBILE] = {
                minHeight: '60px',
                padding: backrs.spacing['spacing-xs'],
                gap: backrs.spacing['spacing-xxs'],
            },
            _b);
    };
};
var cellTopRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '24px',
};
var dayNumberSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-secondary'],
    });
};
var inertNumberSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-quaternary'],
    });
};
var todayBadgeSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: backrs.radius['radius-full'],
            backgroundColor: backrs.colors['Colors/Background/bg-brand-solid']
        },
        _b[MOBILE] = { width: '20px', height: '20px' },
        _b);
};
var todayBadgeTextSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-white'],
    });
};
var kindPillSx = function (kind) {
    return function (_a) {
        var _b;
        var backrs = _a.backrs;
        return (_b = {
                display: 'flex',
                alignItems: 'center',
                gap: backrs.spacing['spacing-xs'],
                minWidth: 0,
                width: '104px',
                padding: "".concat(backrs.spacing['spacing-xs'], " ").concat(backrs.spacing['spacing-md']),
                borderRadius: backrs.radius['radius-sm'],
                border: kind.border ? "1px solid ".concat(backrs.colors[kind.border]) : 'none',
                backgroundColor: backrs.colors[kind.bg],
                color: backrs.colors[kind.fg],
                maxWidth: '100%',
                boxSizing: 'border-box'
            },
            _b[MOBILE] = {
                width: '8px',
                height: '8px',
                padding: 0,
                borderRadius: backrs.radius['radius-full'],
                backgroundColor: backrs.colors[kind.dot],
                '& > *': { display: 'none' },
            },
            _b);
    };
};
var kindPillTextSx = { color: 'inherit', minWidth: 0 };
var focusChipSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            marginTop: 'auto',
            padding: "0 ".concat(backrs.spacing['spacing-xs']),
            borderRadius: backrs.radius['radius-full'],
            border: "1px solid ".concat(backrs.colors['Colors/Border/border-secondary']),
            color: backrs.colors['Colors/Text/text-tertiary'],
            alignSelf: 'flex-start',
            maxWidth: '100%'
        },
        _b[MOBILE] = { display: 'none' },
        _b);
};
var noteChipSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            marginTop: 'auto',
            color: backrs.colors['Colors/Text/text-tertiary'],
            fontStyle: 'italic',
            alignSelf: 'flex-start',
            maxWidth: '100%'
        },
        _b[MOBILE] = { display: 'none' },
        _b);
};
var entryListSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            display: 'flex',
            flexDirection: 'column',
            gap: backrs.spacing['spacing-xxs'],
            minWidth: 0
        },
        _b[MOBILE] = { flexDirection: 'row', flexWrap: 'wrap', gap: backrs.spacing['spacing-xxs'] },
        _b);
};
var entryPillSx = function (tone) {
    return function (_a) {
        var _b;
        var backrs = _a.backrs;
        return (_b = {
                display: 'flex',
                alignItems: 'flex-start',
                gap: backrs.spacing['spacing-xs'],
                minWidth: 0,
                width: '104px',
                maxWidth: '100%',
                padding: "".concat(backrs.spacing['spacing-xs'], " ").concat(backrs.spacing['spacing-md']),
                borderRadius: backrs.radius['radius-sm'],
                border: "1px solid ".concat(backrs.colors[tone.border]),
                backgroundColor: backrs.colors[tone.bg],
                color: backrs.colors[tone.fg],
                boxSizing: 'border-box'
            },
            _b[MOBILE] = {
                width: '8px',
                height: '8px',
                padding: 0,
                borderRadius: backrs.radius['radius-full'],
                backgroundColor: backrs.colors[tone.dot],
                '& > *:not([aria-hidden])': { display: 'none' },
                '& > [aria-hidden]': { display: 'none' },
            },
            _b);
    };
};
var ENTRY_PILL_LINE_HEIGHT = '1rem';
var ENTRY_DOT_SIZE = '6px';
var entryDotSx = function (tone) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            flexShrink: 0,
            marginTop: "calc((".concat(ENTRY_PILL_LINE_HEIGHT, " - ").concat(ENTRY_DOT_SIZE, ") / 2)"),
            width: ENTRY_DOT_SIZE,
            height: ENTRY_DOT_SIZE,
            borderRadius: backrs.radius['radius-full'],
            backgroundColor: backrs.colors[tone.dot],
        });
    };
};
var entryPillTextSx = {
    color: 'inherit',
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    lineHeight: ENTRY_PILL_LINE_HEIGHT,
};
var moreEntriesSx = function (_a) {
    var _b;
    var backrs = _a.backrs;
    return (_b = {
            color: backrs.colors['Colors/Text/text-tertiary'],
            paddingLeft: backrs.spacing['spacing-xxs']
        },
        _b[MOBILE] = { display: 'none' },
        _b);
};
var emptyStateSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        textAlign: 'center',
        color: backrs.colors['Colors/Text/text-tertiary'],
        padding: backrs.spacing['spacing-3xl'],
    });
};
